import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Video, 
  Music, 
  Image, 
  Package, 
  File,
  Clock,
  User,
  CreditCard,
  Gift
} from "lucide-react";
import { DigitalContent, formatPrice, CONTENT_TYPE_LABELS } from "@/types/digitalContent";
import { createDigitalContentPurchase } from "@/services/digitalContent/purchaseService";
import { toast } from "@/hooks/use-toast";

interface PurchaseConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: DigitalContent | null;
  onPurchaseSuccess: () => void;
}

const getContentTypeIcon = (contentType: string) => {
  const icons = {
    pdf: <FileText className="h-5 w-5" />,
    video: <Video className="h-5 w-5" />,
    audio: <Music className="h-5 w-5" />,
    image: <Image className="h-5 w-5" />,
    zip: <Package className="h-5 w-5" />,
    document: <File className="h-5 w-5" />
  };
  return icons[contentType as keyof typeof icons] || <File className="h-5 w-5" />;
};

const getContentTypeColor = (contentType: string): string => {
  const colors = {
    pdf: 'bg-red-500/20 text-red-400 border-red-500/30',
    video: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    audio: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    image: 'bg-green-500/20 text-green-400 border-green-500/30',
    zip: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    document: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  return colors[contentType as keyof typeof colors] || colors.document;
};

export const PurchaseConfirmationModal: React.FC<PurchaseConfirmationModalProps> = ({
  isOpen,
  onClose,
  content,
  onPurchaseSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalState, setModalState] = useState(isOpen);

  // Sincronizar estado do modal com prop isOpen
  useEffect(() => {
    setModalState(isOpen);
    
    // Salvar estado no sessionStorage para persistir entre mudanças de guia
    if (isOpen && content) {
      sessionStorage.setItem('purchaseModal', JSON.stringify({
        isOpen: true,
        contentId: content.id,
        timestamp: Date.now()
      }));
    } else {
      sessionStorage.removeItem('purchaseModal');
    }
  }, [isOpen, content]);

  // Restaurar estado do modal ao voltar para a guia
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const savedState = sessionStorage.getItem('purchaseModal');
        if (savedState) {
          try {
            const parsed = JSON.parse(savedState);
            // Verificar se o estado salvo é recente (menos de 5 minutos)
            if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
              setModalState(true);
            } else {
              sessionStorage.removeItem('purchaseModal');
            }
          } catch (error) {
            sessionStorage.removeItem('purchaseModal');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Prevenir fechamento do modal ao mudar de guia
  const handleOpenChange = (open: boolean) => {
    // Só permite fechar se não estiver processando e for uma ação intencional do usuário
    if (!open && !isProcessing) {
      setModalState(false);
      sessionStorage.removeItem('purchaseModal');
      onClose();
    }
  };

  if (!content) return null;

  const isFree = content.price_cents === 0;

  const handleConfirmPurchase = async () => {
    try {
      setIsProcessing(true);
      
      console.log("🛒 [PurchaseConfirmationModal] Iniciando compra:", {
        contentId: content.id,
        contentTitle: content.title,
        priceCents: content.price_cents,
        isFree: content.price_cents === 0
      });

      // Para conteúdos gratuitos, usar o fluxo direto
      if (isFree) {
        const purchase = await createDigitalContentPurchase(
          content.id,
          content.price_cents
        );
        
        console.log("✅ [PurchaseConfirmationModal] Compra gratuita realizada:", {
          purchaseId: purchase.id,
          paymentStatus: purchase.payment_status,
          accessGrantedAt: purchase.access_granted_at
        });

        toast({
          title: "Conteúdo obtido com sucesso!",
          description: "O conteúdo gratuito foi adicionado à sua biblioteca. Redirecionando...",
          variant: "default"
        });

        // Limpar estado salvo após sucesso
        sessionStorage.removeItem('purchaseModal');
        setModalState(false);
        
        console.log("🔄 [PurchaseConfirmationModal] Redirecionando para /painel-jogador/meus-conteudos em 1.5s");
        
        // Pequeno delay para mostrar a mensagem de sucesso antes do redirecionamento
        setTimeout(() => {
          onPurchaseSuccess();
          onClose();
        }, 1500);

      } else {
        // Para conteúdos pagos, usar sistema unificado
        console.log("💳 [PurchaseConfirmationModal] Usando sistema unificado de pagamentos...");
        
        const purchaseResult = await createDigitalContentPurchase(
          content.id,
          content.price_cents
        );
        
        if (purchaseResult.url) {
          console.log("✅ [PurchaseConfirmationModal] URL de pagamento recebida:", purchaseResult.url);

          toast({
            title: "Redirecionando para pagamento",
            description: "Você será redirecionado para o Stripe para finalizar a compra.",
            variant: "default"
          });

          // Limpar estado do modal
          sessionStorage.removeItem('purchaseModal');
          setModalState(false);
          onClose();

          // Redirecionar para o Stripe
          console.log("🔄 [PurchaseConfirmationModal] Redirecionando para Stripe...");
          window.location.href = purchaseResult.url;
        } else {
          throw new Error("URL de pagamento não foi retornada");
        }
      }

    } catch (error: any) {
      console.error("❌ [PurchaseConfirmationModal] Erro ao processar compra:", error);
      
      let errorMessage = "Ocorreu um erro ao processar sua compra. Tente novamente.";
      
      if (error.message) {
        if (error.message.includes("já possui este conteúdo")) {
          errorMessage = "Você já possui este conteúdo em sua biblioteca.";
        } else if (error.message.includes("compra em andamento")) {
          errorMessage = "Você já tem uma compra em andamento. Aguarde alguns minutos e tente novamente.";
        } else if (error.message.includes("duplicate key")) {
          errorMessage = "Você já possui este conteúdo ou tem uma compra em andamento.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Erro na compra",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={modalState} onOpenChange={(open) => !open && onClose()} modal>
      <DialogContent 
        className="max-w-md max-h-[90vh] overflow-y-auto" 
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isFree ? (
              <>
                <Gift className="h-5 w-5 text-green-500" />
                Obter Conteúdo Gratuito
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 text-[#00B6F0]" />
                Confirmar Compra
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isFree 
              ? "Confirme para adicionar este conteúdo gratuito à sua biblioteca."
              : "Você será redirecionado para o Stripe para finalizar o pagamento com segurança."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Imagem de Capa */}
          {content.cover_image_url && (
            <div className="relative h-32 overflow-hidden rounded-lg">
              <img
                src={content.cover_image_url}
                alt={content.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          )}

          {/* Informações do Conteúdo */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Badge 
                variant="outline" 
                className={`text-xs ${getContentTypeColor(content.content_type)}`}
              >
                {getContentTypeIcon(content.content_type)}
                <span className="ml-1">{CONTENT_TYPE_LABELS[content.content_type]}</span>
              </Badge>
              
              <div className="text-xl font-bold text-[#00B6F0]">
                {formatPrice(content.price_cents)}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-1 text-white">{content.title}</h3>
              <p className="text-sm text-gray-400 line-clamp-2">
                {content.description}
              </p>
            </div>

            {/* Informações do Mentor */}
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <User className="h-4 w-4" />
              <span>Por {content.mentor_name}</span>
            </div>

            {/* Informações adicionais */}
            {content.estimated_duration_minutes && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Clock className="h-4 w-4" />
                <span>Tempo estimado: {content.estimated_duration_minutes} minutos</span>
              </div>
            )}
          </div>

          {/* Resumo da Compra */}
          <div className="bg-[#1A2233] border border-[#2D3748] rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-400">Conteúdo:</span>
              <span className="font-medium text-white">{content.title}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-400">Preço:</span>
              <span className="font-bold text-lg text-[#00B6F0]">
                {formatPrice(content.price_cents)}
              </span>
            </div>
            {!isFree && (
              <div className="text-xs text-gray-500 mt-2">
                * Pagamento processado com segurança pelo Stripe
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmPurchase}
            disabled={isProcessing}
            className="bg-[#00B6F0] hover:bg-[#0099CC] text-black"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                Processando...
              </>
            ) : (
              <>
                {isFree ? (
                  <>
                    <Gift className="h-4 w-4 mr-2" />
                    Obter Gratuito
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pagar com Stripe
                  </>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 